import { getDefaultStore } from "jotai";

import { RadioInstance } from "../Radio";

import CaptionAudioWorker from "./captionAudio.worker?worker";

import { applyCaptionResult, resetCaptionState } from "./captionAtoms";

import type {
  CaptionConfig,
  CaptionEngine,
  CaptionEngineStatus,
  CaptionInstallProgress,
  CaptionResult,
} from "./types";

/**
 * Minimal typing for the `captionsApi` preload bridge (defined in the
 * extension/ preload — owned by the Electron-main component, not here). We
 * declare it locally so the renderer compiles; the real implementation is
 * injected at runtime. In a browser / editor context it is simply absent
 * and the service degrades to a no-op (worker still resamples but nothing
 * consumes the PCM).
 */
declare global {
  interface Window {
    captionsApi?: {
      start: (config: CaptionConfig) => Promise<void>;
      sendPcm: (buf: ArrayBuffer) => void;
      stop: () => void;
      onCaption: (cb: (payload: CaptionResult) => void) => () => void;

      /**
       * Probe an engine's readiness (python / deps / model). Pure check,
       * never touches the network. Used by the config modal to decide
       * between "ready", "needs install" and "no python".
       */
      getStatus: (engine: CaptionEngine) => Promise<CaptionEngineStatus>;

      /**
       * Create the captions venv if missing, pip-install the engine's
       * requirements, then download its model. Progress streams over
       * `onInstallProgress`. Resolves once the whole flow finishes.
       */
      installEngine: (
        engine: CaptionEngine,
      ) => Promise<{ success: boolean; error?: string }>;

      /**
       * Subscribe to install progress; returns an unsubscribe function.
       */
      onInstallProgress: (
        cb: (payload: CaptionInstallProgress) => void,
      ) => () => void;

      /**
       * Current install state in MAIN, so a (re)mounted modal can re-attach to
       * an install already running (progress survives a renderer reload).
       */
      getInstallState: () => Promise<{
        installing: boolean;
        progress: CaptionInstallProgress | null;
      }>;
    };
  }
}

interface StartOptions {
  /** Which ASR backend to load (default applied by callers). */
  engine: CaptionEngine;
  lang: string | null;
  task: "transcribe" | "translate";
  /** How many lines the caption widget renders; drives the rolling commit. */
  maxLines: number;
}

/**
 * Singleton that owns the renderer side of Live Captions.
 *
 * Lifecycle (driven by the widget's `active` flag via the renderer):
 *  - {@link start}: grab the program-mix audio track, spin up the audio
 *    worker (downmix + 48k→16k resample), tell the sidecar to start
 *    (`window.captionsApi.start`), pipe worker PCM → `sendPcm`, and route
 *    incoming captions into `captionTextAtom`.
 *  - {@link stop}: tear everything down (worker terminated, sidecar told to
 *    stop, caption subscription removed, atom reset).
 *
 * Idempotent: there is exactly ONE program stream, so repeated `start`
 * calls only re-apply config (maxLines). Repeated `stop` calls are safe.
 */
class LiveCaptionsService {
  private static _instance: LiveCaptionsService | null = null;

  static getInstance(): LiveCaptionsService {
    if (!LiveCaptionsService._instance) {
      LiveCaptionsService._instance = new LiveCaptionsService();
    }
    return LiveCaptionsService._instance;
  }

  private store = getDefaultStore();

  private worker: Worker | null = null;

  /** Teardown for the `captionsApi.onCaption` subscription. */
  private captionUnsubscribe: (() => void) | null = null;

  private running = false;

  /** Live max-lines used when applying caption results (re-tunable). */
  private maxLines = 2;

  private constructor() {}

  /** Resolve the FULL program-mix audio track (the entire studio sum). */
  private getMixTrack(): MediaStreamTrack | null {
    const globalAudio = RadioInstance?.GlobalAudio;
    if (!globalAudio) return null;
    const track =
      globalAudio.AudioStreamDestination?.stream?.getAudioTracks?.()[0] ?? null;
    return track ?? null;
  }

  async start(options: StartOptions): Promise<void> {
    this.maxLines = Math.max(1, Math.floor(options.maxLines || 1));

    // Idempotent: if already running, only the (cheap) config changes need
    // to apply. We keep the same worker + sidecar session — there is a
    // single program stream by design.
    if (this.running) {
      // Re-send config so an engine/lang/task change while active takes effect.
      try {
        await window.captionsApi?.start({
          engine: options.engine,
          lang: options.lang,
          task: options.task,
        });
      } catch (err) {
        console.warn("[LiveCaptions] re-config failed:", err);
      }
      return;
    }

    const track = this.getMixTrack();
    if (!track) {
      console.warn(
        "[LiveCaptions] no program-mix audio track available; ASR not started",
      );
      return;
    }

    this.running = true;
    resetCaptionState(this.store.set);

    // 1) Subscribe to caption results FIRST so we don't miss early lines.
    if (window.captionsApi) {
      this.captionUnsubscribe = window.captionsApi.onCaption(
        (payload: CaptionResult) => {
          applyCaptionResult(this.store.set, payload, this.maxLines);
        },
      );
    }

    // 2) Tell the sidecar to start (spawns it in main if not running and
    //    writes a type-1 config frame). sampleRate/channels are fixed by
    //    the protocol, so only lang/task travel.
    try {
      await window.captionsApi?.start({
        engine: options.engine,
        lang: options.lang,
        task: options.task,
      });
    } catch (err) {
      console.warn("[LiveCaptions] captionsApi.start failed:", err);
    }

    // 3) Spin up the audio worker and hand it the mix track (transferred).
    try {
      this.worker = new CaptionAudioWorker();
      this.worker.onmessage = (e: MessageEvent) => {
        const msg = e.data;
        if (!msg || typeof msg !== "object") return;
        if (msg.type === "pcm" && msg.buffer instanceof ArrayBuffer) {
          // Forward raw Float32 mono @16k PCM to the sidecar via main.
          window.captionsApi?.sendPcm(msg.buffer);
        } else if (msg.type === "error") {
          console.warn("[LiveCaptions] audio worker error:", msg.error);
        }
      };
      this.worker.onerror = (e) => {
        console.warn("[LiveCaptions] audio worker onerror:", e.message);
      };
      // MediaStreamTrack is transferable in Chromium; transfer it so the
      // worker owns the tap and the render thread never touches the frames.
      this.worker.postMessage({ type: "start", track }, [
        track as unknown as Transferable,
      ]);
    } catch (err) {
      console.error("[LiveCaptions] failed to start audio worker:", err);
      // Roll back so a later start() can retry cleanly.
      void this.stop();
    }
  }

  stop(): void {
    if (!this.running && !this.worker && !this.captionUnsubscribe) return;
    this.running = false;

    // Stop the audio worker.
    if (this.worker) {
      try {
        this.worker.postMessage({ type: "stop" });
      } catch {
        /* ignore */
      }
      const w = this.worker;
      this.worker = null;
      // Give the worker a beat to cancel its reader before terminating.
      setTimeout(() => {
        try {
          w.terminate();
        } catch {
          /* ignore */
        }
      }, 500);
    }

    // Tell the sidecar to stop (type-1 {"cmd":"stop"} frame via main).
    try {
      window.captionsApi?.stop();
    } catch (err) {
      console.warn("[LiveCaptions] captionsApi.stop failed:", err);
    }

    // Drop the caption subscription.
    if (this.captionUnsubscribe) {
      try {
        this.captionUnsubscribe();
      } catch {
        /* ignore */
      }
      this.captionUnsubscribe = null;
    }

    // Clear the displayed caption.
    resetCaptionState(this.store.set);
  }

  isRunning(): boolean {
    return this.running;
  }
}

export default LiveCaptionsService;
